/**
 * useSEO — gestion dynamique des balises SEO par page
 * Gère : <title>, <meta description>, <link canonical>, <meta robots>, JSON-LD
 */
import { useEffect } from "react";

const DEFAULT_TITLE =
  "Hirafi — Trouvez un artisan de confiance en Algérie";
const DEFAULT_DESC =
  "Besoin d'un artisan en Algérie ? Publiez votre demande et recevez des propositions de plombiers, électriciens, maçons et plus dans toutes les wilayas d'Algérie. Application installable sur mobile.";
const BASE_URL = "https://www.hirafidz.com";

interface SEOOptions {
  /** Titre de la page — sera affiché comme "Titre | Hirafi" */
  title?: string;
  /** Meta description (155 car. max recommandés) */
  description?: string;
  /** URL canonique absolue */
  canonical?: string;
  /** true → noindex, nofollow (pages privées / auth) */
  noindex?: boolean;
  /** Locale de la page pour hreflang ('fr' | 'ar') */
  locale?: "fr" | "ar";
}

export function useSEO({
  title,
  description,
  canonical,
  noindex = false,
  locale = "fr",
}: SEOOptions) {
  useEffect(() => {
    // ── Title ──────────────────────────────────────────────────────
    document.title = title ? `${title} | Hirafi` : DEFAULT_TITLE;

    // ── Meta description ───────────────────────────────────────────
    const desc = description || DEFAULT_DESC;
    const metaDesc = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    if (metaDesc) metaDesc.content = desc;

    // ── Canonical ──────────────────────────────────────────────────
    let linkCanon = document.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]'
    );
    if (canonical) {
      const canonHref = canonical.startsWith("http")
        ? canonical
        : `${BASE_URL}${canonical}`;
      if (!linkCanon) {
        linkCanon = document.createElement("link");
        linkCanon.rel = "canonical";
        document.head.appendChild(linkCanon);
      }
      linkCanon.href = canonHref;

      // ── hreflang alternate links ──────────────────────────────────
      // Remplacer /fr/ par /ar/ et vice-versa pour l'alternate
      const altLocale = locale === "fr" ? "ar" : "fr";
      const altHref = canonHref.replace(/\/(fr|ar)\//, `/${altLocale}/`);

      // Nettoyer les hreflang existants
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

      const addHreflang = (lang: string, href: string) => {
        const link = document.createElement("link");
        link.rel = "alternate";
        link.hreflang = lang;
        link.href = href;
        document.head.appendChild(link);
      };
      addHreflang(locale, canonHref);
      addHreflang(altLocale, altHref);
      addHreflang("x-default", canonHref.replace(/\/(fr|ar)\//, "/fr/"));
    } else {
      linkCanon?.remove();
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    }

    // ── Open Graph locale + url (dynamiques selon locale) ─────────
    const ogLocale = document.querySelector<HTMLMetaElement>('meta[property="og:locale"]');
    if (ogLocale) ogLocale.content = locale === "ar" ? "ar_DZ" : "fr_DZ";

    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (ogUrl && canonical) {
      ogUrl.content = canonical.startsWith("http")
        ? canonical
        : `${BASE_URL}${canonical}`;
    }

    // ── Robots ─────────────────────────────────────────────────────
    let metaRobots = document.querySelector<HTMLMetaElement>(
      'meta[name="robots"]'
    );
    if (noindex) {
      if (!metaRobots) {
        metaRobots = document.createElement("meta");
        metaRobots.name = "robots";
        document.head.appendChild(metaRobots);
      }
      metaRobots.content = "noindex, nofollow";
    } else {
      metaRobots?.remove();
    }

    return () => {
      document.title = DEFAULT_TITLE;
      if (metaDesc) metaDesc.content = DEFAULT_DESC;
      document.querySelector('link[rel="canonical"]')?.remove();
      document.querySelector('meta[name="robots"]')?.remove();
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    };
  }, [title, description, canonical, noindex, locale]);
}

/**
 * useJsonLd — injecte un bloc JSON-LD schema.org dans le <head>
 * Idempotent : remplace le script s'il existe déjà (navigation SPA).
 */
export function useJsonLd(data: Record<string, unknown>, id: string) {
  useEffect(() => {
    const scriptId = `jsonld-${id}`;
    document.getElementById(scriptId)?.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = scriptId;
    script.textContent = JSON.stringify(data);
    document.head.appendChild(script);

    return () => {
      document.getElementById(scriptId)?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(data), id]);
}
